import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { io } from "socket.io-client";
import { getUserId } from "../../utils/utilityFunctions/localServices";
import { NODE_URL } from "../../utils/constants";

const SocketContext = createContext({ socket: null, isConnected: false, isLeader: false });
export const useSocket = () => useContext(SocketContext);

const LEADER_KEY = "crm_socket_leader";
const makeTabId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `tab_${Math.random().toString(36).slice(2)}`;

const isDocVisible = () => typeof document !== "undefined" && document.visibilityState === "visible";
// const isWindowFocused = () => typeof document !== "undefined" && document.hasFocus && document.hasFocus();
const isWindowFocused = () => true;

export const SocketProvider = ({ children }) => {
  const user = useSelector((s) => s.user);
  const tabIdRef = useRef(makeTabId());
  const socketRef = useRef(null);

  // Connection/leadership state
  const [isConnected, setIsConnected] = useState(false);
  const [isLeader, setIsLeader] = useState(false);
  const [active, setActive] = useState(isDocVisible() && isWindowFocused()); // visible + focused
  const [leaderId, setLeaderId] = useState(null);

  // Single-flight guard: 'idle' | 'connecting' | 'connected'
  const connectStateRef = useRef("idle");

  const readLeader = () => {
    try {
      const raw = localStorage.getItem(LEADER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const claimLeadership = () => {
    const payload = { tabId: tabIdRef.current, ts: Date.now() };
    localStorage.setItem(LEADER_KEY, JSON.stringify(payload));
    setLeaderId(payload.tabId);
  };

  const relinquishLeadership = () => {
    // Do not erase key; next tab can overwrite. Just flip our local view.
    setLeaderId((prev) => (prev === tabIdRef.current ? null : prev));
  };

  // Keep derived flags
  const hasToken = !!user?.token;
  const amLeader = leaderId === tabIdRef.current;

  // ---- Central connection effect (ONLY place that calls connect/disconnect) ----
  useEffect(() => {
    const shouldConnect = hasToken && active && amLeader;

    if (shouldConnect) {
      // Create instance if needed
      if (!socketRef.current) {
        socketRef.current = io(NODE_URL, {
          transports: ["websocket"],
          autoConnect: false,
          query: { userId: getUserId() },
          // Optional hardening (prevents manager reuse anomalies):
          forceNew: true,
        });

        socketRef.current.on("connect", () => {
          connectStateRef.current = "connected";
          setIsConnected(true);
        });
        socketRef.current.on("disconnect", () => {
          connectStateRef.current = "idle";
          setIsConnected(false);
        });
      }

      if (connectStateRef.current === "idle" && !socketRef.current.connected) {
        connectStateRef.current = "connecting";
        socketRef.current.connect();
      }
    } else {
      // Disconnect if we shouldn't be connected
      if (socketRef.current) {
        if (socketRef.current.connected || connectStateRef.current === "connecting") {
          socketRef.current.disconnect();
        }
        connectStateRef.current = "idle";
        setIsConnected(false);
      }
    }

    return () => {
      // (no-op cleanup here; full cleanup below on unmount/logout)
    };
  }, [hasToken, active, amLeader]);

  // ---- Token changes: cleanup on logout, opportunistic claim on login ----
  useEffect(() => {
    if (!hasToken) {
      relinquishLeadership();
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      connectStateRef.current = "idle";
      setIsConnected(false);
      return;
    }

    // Logged-in and active tab → try to be leader
    if (isDocVisible() && isWindowFocused()) {
      claimLeadership();
    }

    return () => {
      // On provider unmount
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      connectStateRef.current = "idle";
      setIsConnected(false);
      relinquishLeadership();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasToken]);

  // ---- Visibility & focus listeners: only update 'active' and (maybe) claim ----
  useEffect(() => {
    const onVF = () => {
      const nowActive = isDocVisible() && isWindowFocused();
      setActive(nowActive);
      if (hasToken && nowActive) {
        // Race-wins: the last active tab becomes leader
        claimLeadership();
      } else if (!nowActive && amLeader) {
        relinquishLeadership();
      }
    };

    document.addEventListener("visibilitychange", onVF);
    window.addEventListener("focus", onVF);
    window.addEventListener("blur", onVF);
    return () => {
      document.removeEventListener("visibilitychange", onVF);
      window.removeEventListener("focus", onVF);
      window.removeEventListener("blur", onVF);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasToken, amLeader]);

  // ---- Storage: track external leader changes; do NOT connect/disconnect here ----
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== LEADER_KEY) return;
      const leader = readLeader();
      setLeaderId(leader?.tabId || null);

      // If we were leader and lost it, mark local isLeader false; central effect will disconnect.
    };
    window.addEventListener("storage", onStorage);
    // Initialize on mount
    const leader = readLeader();
    setLeaderId(leader?.tabId || null);

    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // ---- Heartbeat: refresh timestamp if we are the leader; don't call connect here ----
  useEffect(() => {
    if (!hasToken) return;
    let t = null;
    const beat = () => {
      if (amLeader && active) {
        localStorage.setItem(LEADER_KEY, JSON.stringify({ tabId: tabIdRef.current, ts: Date.now() }));
      }
      t = window.setTimeout(beat, 5000);
    };
    t = window.setTimeout(beat, 5000);
    return () => t && clearTimeout(t);
  }, [hasToken, amLeader, active]);

  // Expose role flags
  useEffect(() => {
    setIsLeader(amLeader);
  }, [amLeader]);

  const value = useMemo(() => ({ socket: socketRef.current, isConnected, isLeader }), [isConnected, isLeader]);

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};
